import type { CrmFeatures } from '../../../convex/crmFeatures';

export type Lifecycle = 'lead' | 'prospect' | 'customer' | 'supplier' | 'partner' | 'inactive';
export type ScreenPopMode = 'off' | 'mine' | 'all';

export const LIFECYCLE_LABELS: Record<Lifecycle, string> = {
  lead: 'Lead',
  prospect: 'Prospect',
  customer: 'Customer',
  supplier: 'Supplier',
  partner: 'Partner',
  inactive: 'Inactive',
};

export type CustomFieldDefinition = {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select';
  options?: string[];
};

export type CrmContextData = {
  customerId: string;
  features: CrmFeatures;
  settings: {
    callbackSlaMinutes: number;
    vipSlaMinutes: number;
    customFields: CustomFieldDefinition[];
    digestRecipients: string[];
    webhookUrl: string;
    webhookSecretSet: boolean;
    webhookLastStatus?: string;
    webhookLastAtMs?: number;
    aiConsentConfirmedAt?: string;
    aiConsentConfirmedBy?: string;
    aiMinTalkSeconds: number;
    aiDailyLimit: number;
    transcriptRetentionDays: number;
    autoAssignOwner: boolean;
    lookupDailyLimit: number;
  };
  canManage: boolean;
  isStaff: boolean;
  isAdmin: boolean;
  capabilities: { transcription: boolean; email: boolean; ai: boolean; lookup: boolean; lookupGoogle: boolean; lookupWeb: boolean; truecaller: boolean };
  pushPublicKey: string | null;
  prefs: { extension: string; screenPopMode: ScreenPopMode; desktopNotifications: boolean };
};

export type CallStats = {
  totalInteractions: number;
  receivedCalls: number;
  madeCalls: number;
  missedCalls: number;
  answeredCalls: number;
  totalDuration: number;
  lastInteractionAt?: string;
  lastInteractionTimestamp?: number;
};

export type CrmContact = {
  id: string;
  yeastarContactId?: number;
  name: string;
  companyId?: string;
  company?: string;
  email?: string;
  jobTitle?: string;
  phones: Array<{ type: string; number: string }>;
  remark?: string;
  source: 'yeastar' | 'manual' | 'auto';
  syncStatus?: 'synced' | 'pending' | 'error';
  kind: 'contact' | 'caller';
  callerStatus?: 'new' | 'ignored' | 'spam';
  /** Unnamed callers: the name that came with their number (caller ID or the PBX phone book). */
  callerIdName?: string;
  tags: string[];
  lifecycle?: Lifecycle;
  ownerExtension?: string;
  ownerName?: string;
  vip: boolean;
  customFields: Record<string, string>;
  blocked: boolean;
  lastActivityAtMs?: number;
  createdAt: string;
  updatedAt: string;
  stats: CallStats;
};

export type CrmCall = {
  id: string;
  callKey: string;
  contactId?: string;
  companyId?: string;
  number: string;
  direction: 'inbound' | 'outbound';
  answered: boolean;
  voicemail: boolean;
  startedAtMs: number;
  talkSeconds: number;
  answeredBy?: string;
  answeredByName?: string;
  extensions: string[];
  disposition: string;
};

export type CrmTask = {
  id: string;
  kind: 'callback' | 'follow_up';
  status: 'open' | 'done' | 'dismissed';
  title: string;
  details?: string;
  contactId?: string;
  companyId?: string;
  contactName?: string;
  contactKind?: 'contact' | 'caller';
  company?: string;
  vip: boolean;
  phone?: string;
  priority: 'normal' | 'high';
  dueAtMs?: number;
  overdue: boolean;
  assigneeExtension?: string;
  assigneeName?: string;
  source: 'missed_call' | 'manual' | 'insight' | 'ai';
  missedCount?: number;
  firstMissedAtMs?: number;
  lastMissedAtMs?: number;
  voicemail: boolean;
  attemptCount: number;
  lastAttemptAtMs?: number;
  resolution?: 'called_back' | 'caller_reached' | 'manual' | 'dismissed';
  resolvedAtMs?: number;
  resolvedBy?: string;
  responseSeconds?: number;
  createdAtMs: number;
  createdBy?: string;
};

export type CrmNote = {
  id: string;
  body: string;
  authorName: string;
  authorUserId?: string;
  pinned: boolean;
  createdAtMs: number;
  contactId?: string;
  companyId?: string;
};

export type CallSummary = {
  id: string;
  status: 'pending' | 'done' | 'error' | 'skipped';
  summary?: string;
  actionItems: string[];
  sentiment?: 'positive' | 'neutral' | 'negative';
  hasTranscript: boolean;
  error?: string;
};

export type TimelineItem =
  | { type: 'call'; atMs: number; call: CrmCall; summary?: CallSummary }
  | { type: 'note'; atMs: number; note: CrmNote }
  | { type: 'task'; atMs: number; task: CrmTask };

export type CrmCompany = {
  id: string;
  name: string;
  phonePatterns: string[];
  tags: string[];
  lifecycle?: Lifecycle;
  ownerExtension?: string;
  ownerName?: string;
  vip: boolean;
  website?: string;
  createdAt: string;
  updatedAt: string;
  contactCount: number;
  stats: CallStats;
};

/** Who an unknown number probably belongs to (Unknown caller look-up). */
export type NumberLookup = {
  status: 'queued' | 'pending' | 'found' | 'not_found' | 'error';
  name?: string;
  kind?: 'business' | 'person';
  source?: 'google' | 'web';
  sourceUrl?: string;
  detail?: string;
  /** The number was seen on the Google listing or the source page. */
  confirmed: boolean;
  lookedUpAtMs?: number;
  /** Truecaller search from the server (beta; customers in TRUECALLER_SEARCH_CUSTOMERS). */
  truecaller?: {
    status: 'pending' | 'found' | 'not_found' | 'error';
    name?: string;
    company?: string;
    spam?: boolean;
    error?: string;
  } | null;
  truecallerAtMs?: number;
};

export type CallerCard =
  | { number: string; known: false; lookup?: NumberLookup | null }
  | {
      number: string;
      known: true;
      contact: CrmContact;
      lastCall: CrmCall | null;
      lastSpokeWith: { name?: string; extension?: string; atMs: number } | null;
      callsThisWeek: number;
      missedThisWeek: number;
      openTasks: CrmTask[];
      note: CrmNote | null;
      summary?: string;
      lookup?: NumberLookup | null;
    };

export type RecoveryReport = {
  incomplete: boolean;
  missedCalls: number;
  missedCallers: number;
  calledBack: number;
  callerReached: number;
  notRecovered: number;
  pending: number;
  recoveryRate: number | null;
  medianMinutesToRecover: number | null;
  withinSla: number;
  byExtension: Array<{ extension: string; name?: string; recovered: number }>;
  totals: { calls: number; inbound: number; outbound: number; answeredInbound: number; talkSeconds: number };
  /** Callers never reached (or still within the 7 days), most recent first. */
  stillWaiting: Array<{
    partyKey: string;
    firstMissedAtMs: number;
    lastMissedAtMs: number;
    missedCount: number;
    attempts: number;
    outcome: 'not_recovered' | 'pending';
    contactId?: string;
    name: string;
    company?: string;
    kind: 'contact' | 'caller';
    phone?: string;
  }>;
};
